import { type QueryClient, useMutation, useQuery } from "@tanstack/vue-query";
import { getRelationship, Reaction } from "~/src/api";

const fetchUserChapterLike = async (chapterId: string) => {
  if (!import.meta.client) return false;
  const nuxtApp = useNuxtApp();
  const authStore = nuxtApp.$auth();

  await authStore?.waitUntilRefreshIsComplete();
  const token = await authStore?.getToken();
  if (!token || !authStore?.userEntity) {
    return false;
  }

  const reactions = await Reaction.findReactions("novel_chapter", {
    userIds: [authStore.userEntity.id],
    entityIds: [chapterId],
    limit: 1,
  });

  return !!reactions.data.find(
    (r) => getRelationship(r, "novel_chapter").id === chapterId,
  )?.attributes.reactionType;
};

export const useNovelChapterLike = (
  queryClient: QueryClient,
  chapterId: MaybeRefOrGetter<string>,
) => {
  const statsQuery = useQuery(useNovelChapterStats(chapterId));
  const likeOffset = ref(0);

  const queryKey = ["novel", "chapter", chapterId, "like", { authed: true }];
  const likeQuery = useQuery(
    {
      queryKey: queryKey,
      queryFn: async () => {
        const id = toValue(chapterId);
        if (!id) return false;
        return await fetchUserChapterLike(id);
      },
      enabled: computed(() => !!statsQuery.data.value),
    },
    queryClient,
  );

  const likeMutation = useMutation({
    mutationFn: async (like: boolean) => {
      const id = toValue(chapterId);
      if (!id) return like;

      if (like) {
        await Reaction.createReaction(
          "novel_chapter",
          id,
          "like",
          await getTokenOrPrompt(),
        );
      } else {
        await Reaction.deleteReaction(
          "novel_chapter",
          id,
          await getTokenOrPrompt(),
        );
      }
    },
    onMutate: async (like: boolean) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData(queryKey);
      likeOffset.value += like ? 1 : -1;
      // TODO: add a line here to update composite query
      queryClient.setQueryData(queryKey, like);
      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
        likeOffset.value -= !context.previousData ? 1 : -1;
      } else {
        likeOffset.value = 0;
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    likeOffset,
    statsQuery,
    likeQuery,
    likeMutation,
  };
};
